/* @flow */
import * as React from 'react';
import idx from 'idx';
import { graphql, compose } from 'react-apollo';
import { capitalize, clone } from 'lodash';

import type {
  GetOrganizationMember_organizations_members_members,
  GetOrganizationMember_organizations_members_members_user,
} from '../queries/__generated__/GetOrganizationMember.js';
import GET_ORG_MEMBER_QUERY from '../queries/OrganizationMemberQuery.gql';
import UPDATE_USER_NOTE_MUTATION from '../queries/UpdateUserNoteMutation.gql';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator.jsx';
import User from 'entities/User';
import Group from 'entities/Group';
import PeopleListUserProfile from './PeopleListUserProfile.jsx';
import type { UpdateUserNoteInput } from '../queries/__generated__/updateUserNote';
import CurrentUser from 'auth/CurrentUser';

import './PeopleListUserProfile.scss';

type Props = {
  userId: number,
  organizationUuid: string,
  loading: boolean,
  error: errorFragmentFragment,
  orgMember?: GetOrganizationMember_organizations_members_members,
  onBackClicked: ?() => void,
  onGroupClicked: (entityUuid: string) => void,
  onFamilyMemberClicked: (userId: number) => void,
  onClose: () => void,
  mutate: ({
    variables: {
      input: UpdateUserNoteInput,
    },
    refetchQueries: *[],
  }) => Promise<*>,
};

type State = {
  note: string,
};

function subtextForOrgMember(orgMember: GetOrganizationMember_organizations_members_members) {
  const roles = clone(orgMember.roles)
    .sort()
    .join(', ');
  const { grade } = orgMember;

  let subtext = '';
  if (roles && grade) {
    subtext = `${capitalize(roles)}, ${grade}`;
  } else if (roles) {
    subtext = capitalize(roles);
  } else if (grade) {
    subtext = grade;
  }
  return subtext;
}

function adminVisibleDevicesForUser(
  user: GetOrganizationMember_organizations_members_members_user
) {
  return user.adminVisibleDeviceDetails.filter(d => d.type === 'sms' || d.type === 'email');
}

class PeopleListUserProfileDataContainer extends React.Component<Props, State> {
  state = {
    note: '',
  };

  static getDerivedStateFromProps(props: Props) {
    if (props.orgMember) {
      return { note: props.orgMember.user.note };
    }
    return null;
  }

  updateNote = () => {
    const { mutate, userId, organizationUuid } = this.props;

    mutate({
      variables: {
        input: {
          userId,
          note: this.state.note,
        },
      },
      refetchQueries: [
        {
          query: GET_ORG_MEMBER_QUERY,
          variables: { orgUuid: organizationUuid, userId },
        },
      ],
    });
  };

  handleNoteChange = (event: SyntheticEvent<HTMLTextAreaElement>) => {
    this.setState({ note: event.currentTarget.value });
  };

  render() {
    const {
      orgMember,
      loading,
      onBackClicked,
      onGroupClicked,
      onFamilyMemberClicked,
      onClose,
    } = this.props;

    if (loading) {
      return <CenteredLoadingIndicator />;
    }

    if (!orgMember) {
      return null;
    }

    const { user } = orgMember;

    return (
      <PeopleListUserProfile
        user={User.fromJSON(user)}
        familySummary={user.familySummary}
        devices={adminVisibleDevicesForUser(user)}
        groupsOwned={orgMember.groupsOwned.map(g => Group.fromJSON(g))}
        groupsJoined={orgMember.groupsJoined.map(g => Group.fromJSON(g))}
        rostered={orgMember.rosteredStatus}
        canCall={CurrentUser.getUser().hasFeature('phone_calls') && user.canPhone}
        canChat={orgMember.userInteractionPermissions.canChat}
        canAddChild={orgMember.userInteractionPermissions.canAddChild}
        canAddParent={orgMember.userInteractionPermissions.canAddParent}
        subtext={subtextForOrgMember(orgMember)}
        note={this.state.note}
        onNoteChange={this.handleNoteChange}
        onNoteBlur={this.updateNote}
        canChangeNote={user.notable}
        onBackClicked={onBackClicked}
        onGroupClicked={onGroupClicked}
        onFamilyMemberClicked={onFamilyMemberClicked}
        onClose={onClose}
      />
    );
  }
}

const withOrganizationMember = graphql(GET_ORG_MEMBER_QUERY, {
  options: ({ userId, organizationUuid }) => {
    return {
      variables: {
        orgUuid: organizationUuid,
        userId,
      },
    };
  },
  props: ({ data }) => ({
    orgMember: idx(data['organizations'], _ => _[0].members.members[0]),
    loading: data.loading,
    error: data.error,
  }),
  skip: ({ userId, organizationUuid }) => !userId || !organizationUuid,
});

const withUpdateUserNoteMutation = graphql(UPDATE_USER_NOTE_MUTATION);

export { PeopleListUserProfileDataContainer };

export default compose(
  withOrganizationMember,
  withUpdateUserNoteMutation
)(PeopleListUserProfileDataContainer);
